import _extends from "@babel/runtime/helpers/extends";
import React, { useEffect, useState } from "react";
import { Alert, Box, Button, Card, Icon, Modal, Progress, Text } from "~/components-shared";
import { handleError, merge } from "~/lib/helpers";
import Markdown from "~/components/Markdown";
import styled from "styled-components";
import api from "~/lib/api";
import { useTranslation } from "react-i18next";
import StyledIcon from "~/components/StyledIcon";
import { useDispatch, useSelector } from "react-redux";
import insightsSelectors from "~/store/insights/selectors";
import insightsActionCreators from "~/store/insights/actions";
import { InsightsWidgetTypeEnum } from "~/types/enums";
import moment from "moment";
const Overflow = styled.div`
  overflow: hidden;
  max-height: 240px;
  position: relative;
  height: 100%;

  &:after {
    content: "";
    height: 100px;
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    background: ${_ref => {
  let {
    theme
  } = _ref;
  return theme.colors.gradients.background(180);
}};
  }
`;
const StyledButton = styled(Button)`
  position: absolute;
  top: 10px;
  right: 10px;
`;
const LearnMoreButton = styled(Button)`
  position: absolute;
  bottom: 0.5em;
  left: 50%;
  transform: translateX(-50%);
`;
const StyledAlert = styled(Alert)`
  position: absolute;
  width: 100%;
  left: 0;
  bottom: 0;
  overflow: hidden;
  z-index: 2;
`;
const SummaryCard = _ref2 => {
  let {
    dateFrom,
    dateTo,
    topicIds,
    isEditable,
    widget,
    ...rest
  } = _ref2;
  const {
    t
  } = useTranslation();
  const [summary, setSummary] = useState(widget?.data?.summary);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(undefined);
  const [open, setOpen] = React.useState(false);
  const insights = useSelector(insightsSelectors.getInsights);
  const dispatch = useDispatch();
  const handleFetchSummary = function () {
    let noCache = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
    setLoading(true);
    setError(undefined);
    api.fetchInsightsSummary({
      dateFrom,
      dateTo,
      topicIds,
      noCache
    }).then(response => {
      setSummary(response.full);
      const widgets = insights.widgets.map(w => w.type === InsightsWidgetTypeEnum.SUMMARY ? {
        ...w,
        data: {
          summary: response.full,
          summaryShort: response.short,
          updatedAt: moment().toISOString()
        }
      } : w);
      dispatch(insightsActionCreators.exploreSucceeded(merge(insights, {
        widgets
      })));
    }).catch(e => {
      setError(handleError(e));
    }).finally(() => {
      setLoading(false);
    });
  };
  useEffect(() => {
    if (!widget?.data?.summary) {
      handleFetchSummary();
    } else {
      setSummary(widget?.data?.summary);
    }
  }, [dateFrom, dateTo, topicIds, widget?.data?.summary]);
  return /*#__PURE__*/React.createElement(Card, _extends({}, rest, {
    loading: loading
  }), /*#__PURE__*/React.createElement(Text, {
    element: "h3",
    fontSize: 8,
    s: {
      mb: 3
    }
  }, t("SummaryCard.wdyntk")), error && /*#__PURE__*/React.createElement(StyledAlert, {
    severity: "danger",
    fullWidth: true
  }, error), summary && !loading ? /*#__PURE__*/React.createElement(React.Fragment, null, summary === "No line items during this period" ? /*#__PURE__*/React.createElement(Box, {
    direction: "column",
    justify: "center",
    align: "center",
    fullHeight: true
  }, /*#__PURE__*/React.createElement(StyledIcon, {
    src: "infoOutline",
    color: "translucentInfo"
  }), /*#__PURE__*/React.createElement(Text, {
    element: "h3",
    weight: "semiBold",
    display: "block",
    s: {
      mt: 6
    },
    align: "center",
    style: {
      maxWidth: "300px"
    }
  }, "Nothing was identified during this period")) : /*#__PURE__*/React.createElement(Overflow, null, /*#__PURE__*/React.createElement(Markdown, {
    fontSize: 4,
    lineHeight: 6,
    text: summary,
    linkColor: "primary"
  }), summary.length > 400 && /*#__PURE__*/React.createElement(LearnMoreButton, {
    color: "primaryGrey",
    onClick: () => setOpen(true),
    s: {
      px: 4,
      py: 1.5
    },
    fontSize: 3.5,
    rounded: true
  }, "Read more")), isEditable && /*#__PURE__*/React.createElement(StyledButton, {
    "data-tooltip-id": "general-tooltip",
    "data-tooltip-content": t("common.actions.refresh"),
    onClick: () => handleFetchSummary(true),
    color: "translucent",
    outline: false,
    s: {
      p: 2,
      mx: 0.5
    }
  }, /*#__PURE__*/React.createElement(Icon, {
    scale: 5,
    src: "refresh",
    color: "text",
    display: "block"
  }))) : /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Progress, {
    s: {
      mb: 2
    },
    height: 5,
    maxWidth: "100%"
  }), /*#__PURE__*/React.createElement(Progress, {
    s: {
      mb: 2
    },
    height: 5,
    maxWidth: "95%"
  }), /*#__PURE__*/React.createElement(Progress, {
    s: {
      mb: 2
    },
    height: 5,
    maxWidth: "30%"
  }), /*#__PURE__*/React.createElement(Progress, {
    s: {
      mb: 2
    },
    height: 5,
    maxWidth: "80%"
  }), /*#__PURE__*/React.createElement(Progress, {
    s: {
      mb: 2
    },
    height: 5,
    maxWidth: "95%"
  }), /*#__PURE__*/React.createElement(Progress, {
    s: {
      mb: 2
    },
    height: 5,
    maxWidth: "30%"
  }), /*#__PURE__*/React.createElement(Progress, {
    s: {
      mb: 2
    },
    height: 5,
    maxWidth: "100%"
  }), /*#__PURE__*/React.createElement(Progress, {
    s: {
      mb: 2
    },
    height: 5,
    maxWidth: "80%"
  })), /*#__PURE__*/React.createElement(Modal, {
    open: open
  }, /*#__PURE__*/React.createElement(Modal.Body, {
    title: "What you need to know",
    onClose: () => setOpen(false),
    maxWidth: "800px",
    fullWidth: true
  }, /*#__PURE__*/React.createElement(Modal.Content, null, /*#__PURE__*/React.createElement(Markdown, {
    fontSize: 4,
    lineHeight: 6,
    text: summary,
    linkColor: "primary"
  })))));
};
export default SummaryCard;